import { toast as sonner } from "sonner";

/**
 * The app's toast API.
 *
 * A thin wrapper over sonner, not a replacement: sonner already renders, stacks and queues the
 * ~60 existing call sites correctly, and a second notification system would put two stacking
 * contexts in the same corner.
 *
 * It exists for one reason sonner cannot express on its own — `<Toaster duration>` is a single
 * global value, so success and error cannot differ without setting a duration at every call site.
 * Routing every toast through here sets the right one by type and leaves the 60 existing calls
 * untouched apart from which module they import from.
 *
 *   success  3.5s — an acknowledgement. "Contact deleted" needs a glance.
 *   error    6s   — information the user has to read and often act on. Errors also carry a
 *                   close button, so a stack can be cleared rather than waited out.
 */
const SUCCESS_MS = 3_500;
const ERROR_MS = 6_000;

type Options = Parameters<typeof sonner.success>[1];

export const toast = {
  success: (message: string, options?: Options) =>
    sonner.success(message, { duration: SUCCESS_MS, ...options }),

  error: (message: string, options?: Options) =>
    sonner.error(message, { duration: ERROR_MS, ...options }),

  info: (message: string, options?: Options) =>
    sonner.message(message, { duration: SUCCESS_MS, ...options }),

  /** Something the user should notice but that did not fail — treated like an error for
   *  reading time, since it usually needs a decision. */
  warning: (message: string, options?: Options) =>
    sonner.warning(message, { duration: ERROR_MS, ...options }),

  /** Pass-through for the rare call that needs sonner directly (promise toasts, custom JSX). */
  raw: sonner,
};
